import { CircularProgress, Typography } from '@mui/material';
import React, { DragEvent, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { generatedContentApi } from 'src/api/generatedContent';
import styles from './FileUploadField.module.scss';

interface FileUploadFieldProps {
  onFileUploaded: (fileIds: string[]) => void;
  label?: string;
  maxFiles?: number;
}

interface UploadedFile {
  id: string;
  filename: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  onFileUploaded,
  label = 'Upload PDFs for Context',
  maxFiles = 3,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      validateAndSetFiles(fileArray);
    }
  };

  const validateAndSetFiles = (files: File[]) => {
    // Filter out non-PDF files
    const pdfFiles = files.filter((file) => {
      if (file.type !== 'application/pdf') {
        toast.error(`${file.name} is not a PDF file`);
        return false;
      }
      return true;
    });

    const totalFilesAfterAdd =
      uploadedFiles.length + selectedFiles.length + pdfFiles.length;
    const remainingSlots =
      maxFiles - uploadedFiles.length - selectedFiles.length;

    if (totalFilesAfterAdd > maxFiles) {
      toast.error(
        `Only ${remainingSlots} more file(s) can be added. The rest will be ignored.`
      );
      // Append only the allowed number of files
      if (remainingSlots > 0) {
        setSelectedFiles((prevFiles) => [
          ...prevFiles,
          ...pdfFiles.slice(0, remainingSlots),
        ]);
      }
    } else {
      setSelectedFiles((prevFiles) => [...prevFiles, ...pdfFiles]);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploadedFiles.length >= maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileArray = Array.from(e.dataTransfer.files);
      validateAndSetFiles(fileArray);
    }
  };

  const handleFileClick = () => {
    if (
      uploadedFiles.length + selectedFiles.length < maxFiles &&
      !isUploading
    ) {
      fileInputRef.current?.click();
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();

      // Notes: 'pdf' is the key that the backend expects
      selectedFiles.forEach((file) => {
        formData.append('pdf', file);
      });

      const response = await generatedContentApi.uploadPdf(formData);

      const newUploadedFiles = response.map((file) => ({
        id: file.fileId,
        filename: file.filename,
      }));

      const updatedFiles = [...uploadedFiles, ...newUploadedFiles];
      setUploadedFiles(updatedFiles);

      // Clear selectedFiles after successful upload
      setSelectedFiles([]);

      // Update parent component with all file IDs
      onFileUploaded(updatedFiles.map((file) => file.id));
      toast.success(
        `${newUploadedFiles.length} file(s) uploaded successfully!`
      );
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedFiles([]);
  };

  const remainingFilesSlots =
    maxFiles - uploadedFiles.length - selectedFiles.length;

  return (
    <div className={styles.fileUploadContainer}>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${
          uploadedFiles.length >= maxFiles ? styles.disabled : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Typography variant="body2" className={styles.description}>
          Add PDFs to provide additional context for content generation
        </Typography>
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          ref={fileInputRef}
          className={styles.hiddenInput}
          disabled={isUploading || uploadedFiles.length >= maxFiles}
          multiple
        />

        {selectedFiles.length === 0 ? (
          <div className={styles.dropZoneContent}>
            <div className={styles.videoWrapper}>
              <div className={styles.icon}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 16V8M12 8L9 11M12 8L15 11"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.uploadText}>
                {uploadedFiles.length >= maxFiles
                  ? `Maximum ${maxFiles} files reached`
                  : `Select PDFs to upload`}
              </div>
              <div className={styles.dragText}>
                {uploadedFiles.length >= maxFiles
                  ? ''
                  : `Or drag and drop files here (${uploadedFiles.length}/${maxFiles})`}
              </div>
            </div>
            <button
              type="button"
              className={styles.selectButton}
              onClick={handleFileClick}
              disabled={uploadedFiles.length >= maxFiles || isUploading}
            >
              Select PDFs
            </button>
          </div>
        ) : (
          <div className={styles.selectedFilesContainer}>
            <div className={styles.uploadText}>
              {selectedFiles.length} File(s) Selected
            </div>
            <div className={styles.selectedFilesList}>
              {selectedFiles.map((file, index) => (
                <div key={index} className={styles.selectedFile}>
                  <span>{file.name}</span>
                </div>
              ))}
            </div>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.selectButton}
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={24} color="inherit" />
                    <span className={styles.loadingText}>Uploading...</span>
                  </>
                ) : (
                  'Upload Files'
                )}
              </button>

              {!isUploading && (
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCancelSelection}
                >
                  Cancel
                </button>
              )}
            </div>
            {remainingFilesSlots > 0 && !isUploading && (
              <button
                type="button"
                className={styles.addMoreButton}
                onClick={handleFileClick}
              >
                Add More Files ({remainingFilesSlots} remaining)
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadField;
